import React from 'react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  type = 'info'
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          iconBg: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
          icon: '⏹',
          confirmBg: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
          confirmHover: 'linear-gradient(135deg, #ff5252, #e53e3e)'
        };
      case 'warning':
        return {
          iconBg: 'linear-gradient(135deg, #ffd93d, #ffcd02)',
          icon: '⚠️',
          confirmBg: 'linear-gradient(135deg, #ffd93d, #ffcd02)',
          confirmHover: 'linear-gradient(135deg, #ffcd02, #ffc107)'
        };
      default:
        return {
          iconBg: 'linear-gradient(135deg, #4facfe, #00f2fe)',
          icon: '▶️',
          confirmBg: 'linear-gradient(135deg, #4facfe, #00f2fe)',
          confirmHover: 'linear-gradient(135deg, #00f2fe, #4facfe)'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <>
      <style>{`
        @keyframes modalSlideIn {
          from { 
            opacity: 0; 
            transform: translate(-50%, -50%) scale(0.8) translateY(20px);
          }
          to { 
            opacity: 1; 
            transform: translate(-50%, -50%) scale(1) translateY(0);
          }
        }
        
        @keyframes overlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .confirm-button:hover {
          background: ${styles.confirmHover} !important;
          transform: translateY(-1px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
        
        .cancel-button:hover {
          background: #e5e7eb !important;
          transform: translateY(-1px);
        }
        
        .icon-container {
          background: ${styles.iconBg};
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
      
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        animation: 'overlayFadeIn 0.3s ease-out'
      }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '2.5rem',
          maxWidth: '420px',
          width: '90%',
          boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          animation: 'modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <div 
              className="icon-container"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}
            >
              {styles.icon}
            </div>
            
            <h3 style={{
              margin: '0 0 0.75rem 0',
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1a1a1a',
              letterSpacing: '-0.025em'
            }}>
              {title}
            </h3>
            
            <p style={{
              margin: '0 0 2.5rem 0',
              color: '#666',
              lineHeight: '1.6',
              fontSize: '1rem'
            }}>
              {message}
            </p>
            
            <div style={{
              display: 'flex',
              gap: '1rem',
              width: '100%'
            }}>
              <button
                className="cancel-button"
                onClick={onCancel}
                style={{
                  flex: 1,
                  padding: '1rem 1.5rem',
                  background: '#f8f9fa',
                  color: '#495057',
                  border: 'none',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                {cancelText}
              </button>
              <button
                className="confirm-button"
                onClick={onConfirm}
                style={{
                  flex: 1,
                  padding: '1rem 1.5rem',
                  background: styles.confirmBg,
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
                }}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmDialog;
